import { useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Loader } from '@shared/ui/Progress';
import { RegistrySearchBox } from '@shared/ui/RegistryShell';
import { IconRefresh } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import {
  useListTrashQuery,
  useTrashCountsQuery,
  useRestoreTrashMutation,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import {
  TRASH_TYPE_LABELS,
  type TrashEntityType,
  type TrashItem,
} from '@entities/legal/types';

// Срок хранения в «Корзине» до безвозвратной зачистки (backend cleanup).
const RETENTION_DAYS = 30;

// Порядок групп/чипов. Совпадает с TRASH_TYPE_LABELS, задаёт стабильную
// вертикаль независимо от того, какие типы вернул backend.
const TYPE_ORDER: TrashEntityType[] = ['knowledge', 'contract', 'claim', 'lawsuit', 'lna'];

const Note = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Chip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ChipCount = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.muted)};
`;

const SearchRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(5)};

  input {
    width: 100%;
    max-width: 420px;
  }
`;

const Group = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};
`;

const GroupTitle = styled.h2`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const Main = styled.div`
  flex: 1 1 240px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const RowTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const RowSub = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2.5)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Remaining = styled.span<{ $urgent: boolean }>`
  font-weight: 600;
  color: ${({ theme, $urgent }) =>
    $urgent ? theme.colors.status.warning : theme.colors.text.muted};
`;

const RowError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Empty = styled(Card)`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(10)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

const ErrorBox = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(4)};
  border-color: ${({ theme }) => theme.colors.status.danger}44;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

const Center = styled.div`
  padding: ${({ theme }) => theme.spacing(10)};
  text-align: center;
`;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Дней до безвозвратной зачистки = срок хранения − прошедшие дни. */
function daysRemaining(deletedAt: string): number {
  const deleted = new Date(deletedAt).getTime();
  if (Number.isNaN(deleted)) return RETENTION_DAYS;
  const daysPassed = Math.floor((Date.now() - deleted) / 86_400_000);
  return Math.max(0, RETENTION_DAYS - daysPassed);
}

interface TrashRowProps {
  item: TrashItem;
  canWrite: boolean;
}

function TrashRow({ item, canWrite }: TrashRowProps) {
  // Свой инстанс мутации на строку — изолированный isLoading у каждой
  // кнопки «Восстановить» без общего state по всей странице.
  const [restore, { isLoading }] = useRestoreTrashMutation();
  const [error, setError] = useState<string | null>(null);
  const remaining = daysRemaining(item.deleted_at);

  async function onRestore() {
    setError(null);
    try {
      await restore({ entity_type: item.entity_type, id: item.entity_id }).unwrap();
      // При успехе строка исчезнет из списка через инвалидацию тега.
    } catch {
      setError('Не удалось восстановить. Повторите.');
    }
  }

  return (
    <Row>
      <Main>
        <RowTitle>{item.title}</RowTitle>
        {item.subtitle && <RowSub>{item.subtitle}</RowSub>}
        <Meta>
          <Badge $tone="neutral">{TRASH_TYPE_LABELS[item.entity_type]}</Badge>
          <span>Удалено {fmtDate(item.deleted_at)}</span>
          <Remaining $urgent={remaining <= 7}>осталось {remaining} дн.</Remaining>
          {item.deleted_by && <span>· {item.deleted_by}</span>}
        </Meta>
        {error && <RowError role="alert">{error}</RowError>}
      </Main>
      {canWrite && (
        <Button $variant="secondary" $size="sm" onClick={onRestore} disabled={isLoading}>
          <IconRefresh size={14} />
          {isLoading ? 'Восстановление…' : 'Восстановить'}
        </Button>
      )}
    </Row>
  );
}

export function TrashBin() {
  const { can_write } = useAgentPermission('statut');
  const [entityType, setEntityType] = useState<TrashEntityType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);

  const { data: countsData } = useTrashCountsQuery();
  const counts = countsData?.counts ?? [];
  const countByType = new Map<TrashEntityType, number>();
  for (const c of counts) countByType.set(c.entity_type, c.count);
  const total = counts.reduce((sum, c) => sum + c.count, 0);

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useListTrashQuery({
    entity_type: entityType || undefined,
    search: search || undefined,
  });

  const items = data?.items ?? [];
  const groups = TYPE_ORDER.map((type) => ({
    type,
    items: items.filter((i) => i.entity_type === type),
  })).filter((g) => g.items.length > 0);

  const isFiltered = entityType !== '' || search !== '';

  return (
    <div>
      <Note>
        Удалённые документы и записи юр-агента. Хранятся {RETENTION_DAYS} дней, затем удаляются
        безвозвратно. Можно восстановить.
      </Note>

      <Chips role="group" aria-label="Фильтр по типу">
        <Chip
          type="button"
          $active={entityType === ''}
          aria-pressed={entityType === ''}
          onClick={() => setEntityType('')}
        >
          Все
          <ChipCount $active={entityType === ''}>{total}</ChipCount>
        </Chip>
        {TYPE_ORDER.map((type) => (
          <Chip
            key={type}
            type="button"
            $active={entityType === type}
            aria-pressed={entityType === type}
            onClick={() => setEntityType(type)}
          >
            {TRASH_TYPE_LABELS[type]}
            <ChipCount $active={entityType === type}>{countByType.get(type) ?? 0}</ChipCount>
          </Chip>
        ))}
      </Chips>

      <SearchRow>
        <RegistrySearchBox
          type="search"
          aria-label="Поиск по названию"
          placeholder="Поиск по названию…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </SearchRow>

      {isLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : isError ? (
        <ErrorBox role="alert">
          <span>Не удалось загрузить корзину. Возможно, истекла сессия или пропала связь.</span>
          <Button $variant="secondary" $size="sm" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorBox>
      ) : groups.length === 0 ? (
        <Empty>{isFiltered ? 'Ничего не найдено.' : 'Корзина пуста.'}</Empty>
      ) : (
        groups.map((group) => (
          <Group key={group.type}>
            <GroupHead>
              <GroupTitle>{TRASH_TYPE_LABELS[group.type]}</GroupTitle>
              <Badge $tone="neutral">{group.items.length}</Badge>
            </GroupHead>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              {group.items.map((item) => (
                <TrashRow key={item.entity_id} item={item} canWrite={can_write} />
              ))}
            </Card>
          </Group>
        ))
      )}
    </div>
  );
}
